import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { ChartTooltip } from '../ui/charts';
import { CHART_CONFIG } from '../ui/charts/chartTheme';

/**
 * QuotesPipelineDonut — Quote pipeline donut card (MD2 spec).
 *
 * Layout: legend (left) | donut (right, dominant). Center of ring shows total
 * count + "Total quotes" label. Hovering a legend row or a segment emphasizes
 * that segment (siblings fade to 45% opacity, active gets a subtle outline).
 *
 * Cleveland/McGill note: pies/donuts sit in the middle of the perception
 * hierarchy (area encoding). Kept here because the total-to-parts breakdown
 * is the visual story — the total is centered in the ring and legend values
 * show absolute counts + share-of-total.
 *
 * REQ-DSH-04: Click segment or legend row → /reports?report=quote_performance&status=<slug>
 */
export default function QuotesPipelineDonut({ data = { segments: [], total: 0 }, loading = false }) {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(null);
  const isEmpty = !loading && data.total === 0;

  const handleSegmentClick = (entry) => {
    if (entry?.name) {
      navigate(`/reports?report=quote_performance&status=${encodeURIComponent(entry.name.toLowerCase())}`);
    }
  };

  const pctOfTotal = (v) => (data.total > 0 ? Math.round((v / data.total) * 100) : 0);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Quotes Pipeline</h3>
      </div>

      {/* Iter18 Dashboard R2: ChartContainer removed because it wraps children
          in ResponsiveContainer which only works with direct recharts components,
          not a <div> layout. Loading/empty handled inline instead. */}
      {loading ? (
        <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Loading...</p>
        </div>
      ) : isEmpty ? (
        <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>No quote data yet</p>
        </div>
      ) : (
        <div style={bodyStyle}>
          {/* Legend — direct labeling; each row is a keyboard-focusable button */}
          <ul style={legendListStyle}>
            {data.segments.map((seg, idx) => {
              const isActive = activeIdx === idx;
              return (
                <li key={seg.name} style={legendItemStyle}>
                  <button
                    type="button"
                    onClick={() => handleSegmentClick(seg)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseLeave={() => setActiveIdx(null)}
                    onFocus={() => setActiveIdx(idx)}
                    onBlur={() => setActiveIdx(null)}
                    style={{
                      ...legendButtonStyle,
                      background: isActive ? 'var(--neutral-50)' : 'transparent',
                    }}
                    aria-label={`View ${seg.name} quotes (${seg.value}, ${pctOfTotal(seg.value)}% of total)`}
                  >
                    <span aria-hidden="true" style={{ ...legendSwatchStyle, background: seg.fill }} />
                    <div style={legendTextColStyle}>
                      <span style={legendLabelStyle}>{seg.name}</span>
                      <span style={legendPctStyle}>{pctOfTotal(seg.value)}%</span>
                    </div>
                    <span style={legendValueStyle}>{seg.value}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Donut — right-aligned; total + "Total quotes" centered in ring */}
          <div style={donutWrapStyle}>
              <PieChart width={260} height={260}>
                <Pie
                  data={data.segments}
                  cx="50%"
                  cy="50%"
                  innerRadius={82}
                  outerRadius={122}
                  paddingAngle={2}
                  dataKey="value"
                  animationDuration={CHART_CONFIG.animationDuration}
                  onClick={handleSegmentClick}
                  onMouseEnter={(_, idx) => setActiveIdx(idx)}
                  onMouseLeave={() => setActiveIdx(null)}
                  style={{ cursor: 'pointer' }}
                  stroke="var(--color-surface)"
                  strokeWidth={2}
                >
                  {data.segments.map((entry, idx) => {
                    const dimmed = activeIdx !== null && activeIdx !== idx;
                    return (
                      <Cell
                        key={idx}
                        fill={entry.fill}
                        opacity={dimmed ? 0.35 : 1}
                        style={{ transition: 'opacity 0.15s ease-out' }}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  content={
                    <ChartTooltip
                      formatter={(val, name, item) => {
                        const pct = pctOfTotal(val);
                        return `${val} quotes (${pct}%)`;
                      }}
                    />
                  }
                />

                {/* Centered stat: total + label. Not clipped because ring is ~40px thick */}
                <text
                  x="50%"
                  y="46%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontSize: 40, fontWeight: 700, fill: 'var(--color-text-default)' }}
                >
                  {data.total}
                </text>
                <text
                  x="50%"
                  y="58%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fill: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                  }}
                >
                  Total quotes
                </text>
              </PieChart>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Styles ----------

const containerStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: 'var(--card-radius)',
  // PT-41.2 (2026-07-27): asymmetric horizontal padding gives the donut +
  // legend room to breathe on the right and the title room to line up with
  // adjacent cards on the left. Operator ruling: left ≈40px / right ≈80px;
  // top/bottom keep the prior 20px. Vertical padding NOT tokenised here —
  // this container is the only one with this shape.
  padding: '20px 80px 20px 40px',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  marginBottom: 12,
};

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color-text-default)',
};

const bodyStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 220px) 1fr',
  gap: 24,
  alignItems: 'center',
  minHeight: 260,
};

const legendListStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const legendItemStyle = { margin: 0 };

const legendButtonStyle = {
  display: 'grid',
  gridTemplateColumns: '12px 1fr auto',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '6px 8px',
  border: 'none',
  cursor: 'pointer',
  borderRadius: 'var(--btn-radius)',
  fontSize: 13,
  color: 'var(--color-text-default)',
  textAlign: 'left',
  transition: 'background 0.15s ease-out',
};

const legendSwatchStyle = {
  width: 10,
  height: 10,
  borderRadius: 2,
  display: 'inline-block',
};

const legendTextColStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minWidth: 0,
};

const legendLabelStyle = {
  fontWeight: 500,
  color: 'var(--color-text-default)',
};

const legendPctStyle = {
  fontSize: 11,
  color: 'var(--color-text-muted)',
  fontVariantNumeric: 'tabular-nums',
};

const legendValueStyle = {
  fontWeight: 700,
  color: 'var(--color-text-default)',
  fontVariantNumeric: 'tabular-nums',
  fontSize: 14,
};

const donutWrapStyle = {
  minWidth: 0,
  display: 'flex',
  justifyContent: 'flex-end',
};
